import { useCallback, useEffect, useMemo, useState } from "react";
import useApi from "./useApi";
import {
  getPostIfCanHire,
  getPostIfClients,
  getPostIfDepartments,
  getPostIfPositions,
  getPostIfRateTypes,
  getPostIfReportToUsers,
  getPostIfServices,
} from "../services/pifService";
import { PostIfCanHireResultDto, PostIfLookupItemDto } from "../types/pifDto";

interface UsePifReferenceDataArgs {
  departmentId: number | null;
  serviceId: number | null;
}

/**
 * Fronts the seven PostIF reference-data routes.
 *
 * The department-scoped lists reload when the department changes and clients reload when
 * the service does, which is what makes "Department gates Position" real rather than a
 * client-side filter over one big payload. The three unscoped lists load once.
 */
const usePifReferenceData = ({ departmentId, serviceId }: UsePifReferenceDataArgs) => {
  const { executeApi } = useApi();
  const [departments, setDepartments] = useState<PostIfLookupItemDto[]>([]);
  const [allPositions, setAllPositions] = useState<PostIfLookupItemDto[]>([]);
  const [hireScope, setHireScope] = useState<number[] | null>(null);
  const [reportToUsers, setReportToUsers] = useState<PostIfLookupItemDto[]>([]);
  const [services, setServices] = useState<PostIfLookupItemDto[]>([]);
  const [clients, setClients] = useState<PostIfLookupItemDto[]>([]);
  const [rateTypes, setRateTypes] = useState<PostIfLookupItemDto[]>([]);

  // Unscoped lists — fetched once for the life of the form.
  useEffect(() => {
    let active = true;

    const load = async () => {
      const [depts, rates, canHire] = await Promise.all([
        executeApi<undefined, PostIfLookupItemDto[]>(getPostIfDepartments, undefined),
        executeApi<undefined, PostIfLookupItemDto[]>(getPostIfRateTypes, undefined),
        executeApi<undefined, PostIfCanHireResultDto>(getPostIfCanHire, undefined),
      ]);
      if (!active) return;
      if (depts) setDepartments(depts);
      if (rates) setRateTypes(rates);
      // An empty scope means the API couldn't narrow it (the interim Can Hire rule
      // reports "both" with no per-position data), so don't filter on nothing.
      setHireScope(canHire?.scope?.length ? canHire.scope.map((s) => s.positionId) : null);
    };

    void load();
    return () => {
      active = false;
    };
  }, [executeApi]);

  // Department-scoped lists.
  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!departmentId) {
        setAllPositions([]);
        setReportToUsers([]);
        setServices([]);
        return;
      }
      const [pos, users, svcs] = await Promise.all([
        executeApi<number, PostIfLookupItemDto[]>(getPostIfPositions, departmentId),
        executeApi<number, PostIfLookupItemDto[]>(getPostIfReportToUsers, departmentId),
        executeApi<number, PostIfLookupItemDto[]>(getPostIfServices, departmentId),
      ]);
      if (!active) return;
      setAllPositions(pos ?? []);
      setReportToUsers(users ?? []);
      setServices(svcs ?? []);
    };

    void load();
    return () => {
      active = false;
    };
  }, [executeApi, departmentId]);

  // Clients are scoped to the chosen service, not the department.
  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!serviceId) {
        setClients([]);
        return;
      }
      const list = await executeApi<number, PostIfLookupItemDto[]>(getPostIfClients, serviceId);
      if (!active) return;
      setClients(list ?? []);
    };

    void load();
    return () => {
      active = false;
    };
  }, [executeApi, serviceId]);

  /**
   * "Only what you're cleared to hire into" — the department's positions narrowed to the
   * caller's Can Hire scope, when the API gave us one.
   */
  const positions = useMemo(
    () => (hireScope ? allPositions.filter((p) => hireScope.includes(p.id)) : allPositions),
    [allPositions, hireScope],
  );

  /** Resolves a selected id back to its display text, for the summary views. */
  const textFor = useCallback(
    (list: PostIfLookupItemDto[], id: number | null): string => list.find((i) => i.id === id)?.text ?? "",
    [],
  );

  return { departments, positions, reportToUsers, services, clients, rateTypes, textFor };
};

export default usePifReferenceData;
